import React, { useEffect, useRef, useState } from 'react'
import cn from 'classnames'
import { message } from 'antd'
import requests from '../../../axios/requests'
import { plural, pluralN } from './genUtils'
import { QuestionBody, TypeBar, TYPE_LABEL, TYPE_ORDER, TYPE_TITLE } from './TestQuestion'
import TestReview from './TestReview'
import LangFlag, { LANG_NAME } from './LangFlag'
import s from './gen.module.scss'
import t from './testgen.module.scss'

const COUNTS = [10, 20, 30]
const LEVELS = [
  ['easy', 'Базовая', 'Определения и факты — для входного контроля'],
  ['mid', 'Средняя', 'Понимание и применение — для итогового теста'],
  ['hard', 'Продвинутая', 'Разбор примеров и нестандартные случаи — для сильных групп'],
]
const LEVEL_LABEL = { easy: 'базовая', mid: 'средняя', hard: 'продвинутая' }
const LANGS = ['RU', 'EN', 'SA']
const asList = (data) => (Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [])
const errText = (err, fallback) => {
  const d = err?.response?.data
  return (Array.isArray(d) && d[0]) || d?.detail || d?.non_field_errors?.[0] || fallback
}
const busyDraft = (d) => d.status === 'generating' || (d.questions || []).some((q) => q.state)
const timeOf = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

const Box = ({ on }) => (
  <span className={t.box} aria-hidden="true">
    {on && (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4">
        <path d="M5 12l5 5L20 7" />
      </svg>
    )}
  </span>
)

// Режим «Только тест» шага 2: настройки нового варианта слева, варианты (до slots) справа.
const TestGen = ({ byDoc, tag, doc, docTitle, limit, slots, onCreated, onBalance }) => {
  const [drafts, setDrafts] = useState([])
  const [active, setActive] = useState(0)
  const [count, setCount] = useState(20)
  const [types, setTypes] = useState({ single: true, ordering: true, matching: false })
  const [level, setLevel] = useState('mid')
  const [lang, setLang] = useState('RU')
  const [strict, setStrict] = useState(true)
  const [outline, setOutline] = useState({ status: 'idle', sections: [] })
  const [picked, setPicked] = useState(null) // null = все разделы
  const [starting, setStarting] = useState(false)
  const [creating, setCreating] = useState(false)
  const [review, setReview] = useState(false)
  const pollRef = useRef(null)
  const outlineRef = useRef(null)
  const sourceKey = byDoc ? `pdf:${doc?.id}` : `tag:${tag?.id}`
  const params = byDoc ? { pdf: doc?.id } : { speciality_tag: tag?.id }

  const fetchDrafts = () => {
    if (!(byDoc ? doc?.id : tag?.id)) return Promise.resolve([])
    return requests.users
      .quiz_drafts(params)
      .then((r) => {
        const list = asList(r.data)
        setDrafts(list)
        return list
      })
      .catch((err) => {
        console.error(err)
        return []
      })
  }

  useEffect(() => {
    setDrafts([])
    setActive(0)
    setPicked(null)
    fetchDrafts().then((list) => setActive(Math.max(0, list.length - 1)))
    return () => clearTimeout(pollRef.current)
  }, [sourceKey])

  // пока вариант генерируется или меняются вопросы — перечитываем каждые 3 с
  useEffect(() => {
    clearTimeout(pollRef.current)
    if (drafts.some(busyDraft)) pollRef.current = setTimeout(fetchDrafts, 3000)
    return () => clearTimeout(pollRef.current)
  }, [drafts])

  // разделы документа считает модель — первый раз до минуты, дальше из кеша
  useEffect(() => {
    clearTimeout(outlineRef.current)
    if (!byDoc || !doc?.id) return
    let alive = true
    let tries = 0
    setOutline({ status: 'pending', sections: [] })
    const load = () =>
      requests.users
        .quiz_doc_outline(doc.id)
        .then(({ data }) => {
          if (!alive) return
          if (data.status === 'pending' && tries++ < 60) {
            outlineRef.current = setTimeout(load, 3000)
            return
          }
          setOutline({ status: data.status === 'ready' ? 'ready' : 'failed', sections: data.sections || [] })
        })
        .catch(() => alive && setOutline({ status: 'failed', sections: [] }))
    load()
    return () => {
      alive = false
      clearTimeout(outlineRef.current)
    }
  }, [byDoc, doc?.id])

  const chosenTypes = TYPE_ORDER.filter((k) => types[k])
  const noTypes = !chosenTypes.length
  const generatingNow = drafts.some((d) => d.status === 'generating')
  const full = drafts.length >= slots
  const sections = outline.sections || []
  const pickedSet = picked || sections.map((x) => x.index)
  const noSections = byDoc && outline.status === 'ready' && sections.length > 0 && !pickedSet.length
  const canStart = !noTypes && !noSections && !full && !generatingNow && !starting && limit > 0
  const startNote = full
    ? `Все ${pluralN(slots, ['вариант', 'варианта', 'вариантов'])} созданы`
    : limit === 0
    ? 'Пополните баланс генераций, чтобы получить вариант'
    : generatingNow
    ? 'Дождитесь, пока сгенерируется текущий вариант'
    : 'Бесплатно, около минуты. Генерация спишется, только когда создадите тест.'

  const start = () => {
    if (!canStart) return
    setStarting(true)
    const all = picked === null || pickedSet.length === sections.length
    requests.users
      .quiz_draft_create({
        ...params,
        question_count: count,
        types: chosenTypes,
        level,
        language: lang.toLowerCase(),
        sections: byDoc && !all ? pickedSet : [],
        strict,
      })
      .then(({ data }) => {
        setDrafts((prev) => [...prev, data])
        setActive(drafts.length)
      })
      .catch((err) => message.error(errText(err, 'Не удалось запустить генерацию теста')))
      .finally(() => setStarting(false))
  }

  const replaceDraft = (data) => setDrafts((prev) => prev.map((d) => (d.id === data.id ? data : d)))
  const act = (draft, name, body) =>
    requests.users
      .quiz_draft_action(draft.id, name, body)
      .then(({ data }) => replaceDraft(data))
      .catch((err) => message.error(errText(err, 'Не получилось — попробуйте ещё раз')))

  const createTest = (draft) => {
    if (limit === 0) return message.error('Закончились генерации — пополните баланс')
    setCreating(true)
    requests.users
      .quiz_draft_action(draft.id, 'create_test')
      .then(({ data }) => {
        replaceDraft(data)
        setReview(false)
        onBalance && onBalance()
        onCreated && onCreated(data)
      })
      .catch((err) => message.error(errText(err, 'Не удалось создать тест')))
      .finally(() => setCreating(false))
  }

  const removeDraft = (draft) =>
    requests.users
      .quiz_draft_delete(draft.id)
      .then(() => fetchDrafts().then((list) => setActive(Math.max(0, list.length - 1))))
      .catch((err) => message.error(errText(err, 'Не удалось удалить вариант')))

  const draft = drafts[Math.min(active, drafts.length - 1)]
  const nextN = Math.min(drafts.length + 1, slots)

  const settings = (
    <div className={t.panel}>
      <div>
        <div style={{ fontWeight: 600, fontSize: 17 }}>Настройки нового варианта</div>
        <div className={cn(s.muted, s.small)}>
          {byDoc && outline.status === 'ready'
            ? `ИИ нашёл в документе ${pluralN(sections.length, ['раздел', 'раздела', 'разделов'])}`
            : 'Уже готовые варианты не меняются — у каждого свои настройки'}
        </div>
      </div>
      <div className={t.lbl}>
        Количество вопросов
        <div className={s.seg} role="radiogroup" aria-label="Количество вопросов">
          {COUNTS.map((n) => (
            <button
              type="button"
              key={n}
              role="radio"
              aria-checked={count === n}
              className={cn({ [s.segOn]: count === n })}
              onClick={() => setCount(n)}
            >
              {n}
            </button>
          ))}
        </div>
      </div>
      <div className={t.lbl}>
        Типы вопросов
        <div className={t.checks}>
          {TYPE_ORDER.map((k) => (
            <button
              type="button"
              key={k}
              aria-pressed={!!types[k]}
              className={cn(t.ck, { [t.ckOn]: types[k] })}
              onClick={() => setTypes({ ...types, [k]: !types[k] })}
            >
              <Box on={types[k]} />
              {TYPE_TITLE[k]}
            </button>
          ))}
        </div>
        {noTypes && <span className={t.warn}>Выберите хотя бы один тип</span>}
      </div>
      {!byDoc && (
        <div className={t.lbl}>
          Сложность
          <div className={s.seg} role="radiogroup" aria-label="Сложность">
            {LEVELS.map(([k, label]) => (
              <button
                type="button"
                key={k}
                role="radio"
                aria-checked={level === k}
                className={cn({ [s.segOn]: level === k })}
                onClick={() => setLevel(k)}
              >
                {label}
              </button>
            ))}
          </div>
          <span className={t.hint}>{LEVELS.find((x) => x[0] === level)[2]}</span>
        </div>
      )}
      {byDoc && (
        <div className={t.lbl}>
          Разделы
          {outline.status === 'pending' ? (
            <span className={t.hint}>ИИ читает документ и выделяет разделы… обычно до минуты</span>
          ) : outline.status === 'failed' || !sections.length ? (
            <span className={t.hint}>Разделы выделить не удалось — вопросы будут по всему документу</span>
          ) : (
            <div className={t.sections}>
              {sections.map((sec) => {
                const on = pickedSet.includes(sec.index)
                return (
                  <label key={sec.index}>
                    <span>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() =>
                          setPicked(on ? pickedSet.filter((x) => x !== sec.index) : [...pickedSet, sec.index])
                        }
                      />
                      {sec.title}
                    </span>
                    {sec.pages && <span className={s.muted}>с. {sec.pages}</span>}
                  </label>
                )
              })}
            </div>
          )}
          {noSections && <span className={t.warn}>Отметьте хотя бы один раздел</span>}
        </div>
      )}
      {byDoc && (
        <label className={t.switchRow}>
          <input type="checkbox" checked={strict} onChange={() => setStrict(!strict)} />
          <span className={cn(t.switch, { [t.switchOn]: strict })} aria-hidden="true" />
          <span>
            <span style={{ display: 'block', fontWeight: 600, fontSize: 14 }}>Строго по документу</span>
            <span className={cn(s.muted, s.small)}>Не добавлять факты, которых нет в файле</span>
          </span>
        </label>
      )}
      <div className={t.lbl}>
        Язык
        <div className={s.seg} role="radiogroup" aria-label="Язык теста">
          {LANGS.map((l) => (
            <button
              type="button"
              key={l}
              role="radio"
              aria-checked={lang === l}
              className={cn({ [s.segOn]: lang === l })}
              onClick={() => setLang(l)}
              title={LANG_NAME[l]}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <LangFlag lang={l} />
              {l}
            </button>
          ))}
        </div>
      </div>
      <div className={t.foot}>
        <button type="button" className={cn(s.btn, s.pri)} disabled={!canStart} onClick={start}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M12 3l1.8 4.7L18.5 9.5 13.8 11.3 12 16l-1.8-4.7L5.5 9.5l4.7-1.8z" />
          </svg>
          {starting ? 'Запускаем…' : full ? 'Варианты закончились' : `Сгенерировать вариант ${nextN} из ${slots}`}
        </button>
        <div className={cn(s.muted, s.small, t.center)}>{startNote}</div>
      </div>
    </div>
  )

  const tabs = drafts.length > 1 && (
    <div className={t.tabs} role="tablist" aria-label="Варианты теста">
      {drafts.map((d, i) => (
        <button
          type="button"
          key={d.id}
          role="tab"
          aria-selected={draft?.id === d.id}
          className={cn(t.tab, { [t.tabOn]: draft?.id === d.id })}
          onClick={() => setActive(i)}
          title={d.quest ? 'Тест создан' : d.status === 'generating' ? 'Генерируется' : ''}
        >
          {i + 1}
          {d.status === 'generating' && <span className={t.tabDot} />}
        </button>
      ))}
    </div>
  )

  let variant
  if (!draft) {
    variant = (
      <div className={t.variant}>
        <div className={t.emptyV}>
          <b style={{ color: 'var(--g-text)' }}>Здесь появится вариант теста</b>
          <span>Задайте настройки слева и нажмите «Сгенерировать» — ИИ подберёт темы и вопросы с ответами</span>
        </div>
      </div>
    )
  } else if (draft.status === 'generating') {
    const ready = (draft.questions || []).length
    variant = (
      <article className={t.variant} aria-busy="true">
        <div className={t.vHead}>
          <h3>Вариант {drafts.indexOf(draft) + 1}</h3>
          {tabs}
        </div>
        <div className={t.tags}>
          <span className={t.tag} style={{ background: 'var(--g-accent-soft)', color: 'var(--g-accent-text)' }}>
            Генерируется · {ready} из {draft.question_count}
          </span>
        </div>
        <div className={t.progress}>
          <i style={{ width: `${Math.max(6, (100 * ready) / draft.question_count)}%` }} />
        </div>
        <div className={cn(s.muted, s.small)}>
          {draft.topics?.length
            ? `Темы: ${draft.topics.map((x) => x.name).join(', ')}`
            : byDoc
            ? 'ИИ читает документ и подбирает вопросы'
            : 'ИИ подбирает темы теста'}
        </div>
        <div className={cn(s.muted, s.small)} style={{ marginTop: 'auto' }}>
          Обычно 1–2 минуты. Можно уйти со страницы — вариант сохранится в этом {byDoc ? 'документе' : 'навыке'}.
        </div>
      </article>
    )
  } else if (draft.status === 'failed') {
    variant = (
      <article className={t.variant}>
        <div className={t.vHead}>
          <h3>Вариант {drafts.indexOf(draft) + 1}</h3>
          {tabs}
        </div>
        <div className={t.emptyV}>
          <b style={{ color: 'var(--g-text)' }}>Не получилось сгенерировать вариант</b>
          <span>{draft.error || 'Модель не ответила'}. Генерации не списаны.</span>
          <button type="button" className={cn(s.btn, s.sec)} onClick={() => removeDraft(draft)}>
            Удалить вариант и попробовать снова
          </button>
        </div>
      </article>
    )
  } else {
    const qs = (draft.questions || []).filter((q) => q.state !== 'adding')
    const example = qs.find((q) => q.type === 'single') || qs[0]
    const exampleN = example ? qs.indexOf(example) + 1 : 0
    const created = !!draft.quest
    variant = (
      <article className={cn(t.variant, t.variantOn)}>
        <div className={t.vHead}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <h3>Вариант {drafts.indexOf(draft) + 1}</h3>
            <span className={cn(s.muted, s.small)}>готов · {timeOf(draft.created_at)}</span>
          </div>
          {tabs}
        </div>
        <div className={t.tags}>
          {created && <span className={cn(t.tag, t.tagOk)}>✓ Тест создан</span>}
          <span className={t.tag}>{pluralN(qs.length, ['вопрос', 'вопроса', 'вопросов'])}</span>
          {!draft.pdf && <span className={t.tag}>{LEVEL_LABEL[draft.level]}</span>}
          {draft.pdf && draft.strict && <span className={t.tag}>строго по документу</span>}
          <span className={t.tag}>
            <LangFlag lang={(draft.language || 'ru').toUpperCase()} />
            {(draft.language || 'ru').toUpperCase()}
          </span>
          <span className={t.tag}>~{draft.minutes} мин на прохождение</span>
        </div>
        <TypeBar counts={draft.type_counts} />
        <div className={t.rows}>
          <div className={cn(s.muted, s.small)} style={{ fontWeight: 600, paddingBottom: 2 }}>
            {byDoc ? 'Разделы документа' : `Покрывает ${pluralN(draft.topics.length, ['тему', 'темы', 'тем'])}`}
          </div>
          {draft.topics.map((tp) => (
            <div key={tp.name} className={t.row}>
              <span>
                {tp.name}
                {tp.pages ? <span className={s.muted}> · с. {tp.pages}</span> : null}
              </span>
              <span className={s.muted}>{pluralN(tp.count, ['вопрос', 'вопроса', 'вопросов'])}</span>
            </div>
          ))}
        </div>
        {example && (
          <div className={t.example}>
            <div className={t.exHead}>
              <span>
                Вопрос {exampleN} · {TYPE_LABEL[example.type]}
              </span>
              <span>{example.source?.page ? `Источник: с. ${example.source.page}` : example.topic}</span>
            </div>
            <div className={t.qText}>{example.question}</div>
            <QuestionBody q={example} />
            {example.source?.quote && <blockquote className={t.quote}>«{example.source.quote}»</blockquote>}
          </div>
        )}
        <div className={t.actions}>
          {created ? (
            <>
              <button type="button" className={cn(s.btn, s.sec)} onClick={() => setReview(true)}>
                Посмотреть вопросы
              </button>
              <button type="button" className={cn(s.btn, s.pri)} onClick={() => onCreated && onCreated(draft, true)}>
                Открыть тест
              </button>
            </>
          ) : (
            <>
              <button type="button" className={cn(s.btn, s.sec)} onClick={() => setReview(true)}>
                Проверить {qs.length} {plural(qs.length, ['вопрос', 'вопроса', 'вопросов'])}
              </button>
              <button
                type="button"
                className={cn(s.btn, s.pri)}
                disabled={creating || busyDraft(draft) || limit === 0}
                onClick={() => createTest(draft)}
              >
                {creating ? 'Создаём тест…' : 'Создать тест · 1 генерация'}
              </button>
            </>
          )}
        </div>
        <div className={cn(s.muted, s.small)}>
          {created
            ? 'Тест — это курс из одного занятия: публикуйте его для групп или вставьте в другой курс на шаге «Созданные».'
            : 'После создания тест откроется в редакторе: там задаются проходной балл, число попыток и время.'}
        </div>
      </article>
    )
  }

  return (
    <>
      <div className={t.wrap}>
        {settings}
        {variant}
      </div>
      {draft && draft.status === 'ready' && (
        <TestReview
          open={review}
          draft={draft}
          title={byDoc ? docTitle : tag?.name}
          number={drafts.indexOf(draft) + 1}
          creating={creating}
          readOnly={!!draft.quest}
          onClose={() => setReview(false)}
          onReplace={(q) => act(draft, 'replace_question', { question_id: q.id })}
          onRemove={(q) => act(draft, 'remove_question', { question_id: q.id })}
          onAdd={(topic, type) => act(draft, 'add_question', { topic, type })}
          onCreate={() => createTest(draft)}
        />
      )}
    </>
  )
}

export default TestGen

// «Что создаём?» - две карточки-радио над вариантами шага 2.
export const KindPicker = ({ value, onChange }) => (
  <div className={t.kinds} role="radiogroup" aria-label="Что создаём">
    <button
      type="button"
      role="radio"
      aria-checked={value === 'course'}
      className={cn(t.kind, { [t.kindOn]: value === 'course' })}
      onClick={() => onChange('course')}
    >
      <span className={t.kindIco}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z" />
          <path d="M4 21V5M9 7h6M9 11h6" />
        </svg>
      </span>
      <span>
        <b>Курс</b>
        <span className={cn(s.muted, s.small)}>Модули, статьи, задания и тесты. Материалы дописываются в фоне.</span>
      </span>
    </button>
    <button
      type="button"
      role="radio"
      aria-checked={value === 'test'}
      className={cn(t.kind, { [t.kindOn]: value === 'test' })}
      onClick={() => onChange('test')}
    >
      <span className={t.kindIco}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <rect x="4" y="3" width="16" height="18" rx="2" />
          <path d="M8 8l1.5 1.5L12 7M8 15l1.5 1.5L12 14M14 8.5h3M14 15.5h3" />
        </svg>
      </span>
      <span>
        <b>
          Только тест<span className={t.badgeNew}>новое</span>
        </b>
        <span className={cn(s.muted, s.small)}>
          Вопросы с ответами для проверки знаний — отдельно или как этап курса.
        </span>
      </span>
    </button>
  </div>
)
